import { PageType } from "./state-machine/page-recognise";
import {
  ActionType,
  IAdditionalSettings,
  IntelligentSelector,
  PerformanceMetrics,
  ProductPage,
  ProductVariants,
  SiteSuitabilityResult,
  SkuOption,
  UrlPattern,
} from "./state-machine/state-machine";

export interface IMerchantSchedule {
  enabled: boolean;
  cronExpression: string;
  lastRun?: Date;
  nextScheduleRun?: Date;
  timezone: string;
  lockTime?: Date;
  processingLock?: boolean;

  // Audit schedule for testing pending/suspended coupons
  auditCronExpression?: string;
  auditNextScheduleRun?: Date;
  auditLastRun?: Date;
}

/**
 * How a merchant's scheduled runs execute:
 * - "agentic": full browser navigation state machine (default)
 * - "api": platform API handlers (Shopify, WooCommerce, …)
 * - "session": replay the captured extension session + run the extension engine
 *
 * Legacy merchants without the field are inferred from checkoutType
 * (OTHER → agentic, anything else → api). Set to "session" automatically
 * when a captured session is linked to the merchant.
 */
export type TMerchantRunMode = "agentic" | "api" | "session";

/** Independent per-half lifecycle for whether an AI-built template exists yet. */
export type TTemplateCreationStatus = "not_created" | "created" | "failed";

export interface IUpsertMerchant {
  name: string;
  checkoutType: checkoutTechnologyType;
  countryCode: string;
  homePageUrl: string;

  runMode?: TMerchantRunMode | null;

  removableInvalidCoupons?: boolean; // when a coupon is entered but is invalid, we must still remove it

  // Business Logic
  minimumCartValue?: number;
  recommendedTestProducts?: string[];

  // Timestamps
  createdAt?: Date;
  updatedAt?: Date;

  discoveredSelectors?: {
    [key in ActionType]?: IntelligentSelector[];
  };

  productVariants?: ProductVariants[];

  urlPatterns?: {
    [key in PageType]?: Array<UrlPattern>;
  };

  performanceMetrics?: PerformanceMetrics;

  cachedProductPages?: ProductPage[];
  additionalSettings?: IAdditionalSettings;
  schedule?: IMerchantSchedule;

  wooCommerceMetadata?: IWooCommerceMerchantMetadata;
  bigCommerceMetadata?: IBigCommerceMerchantMetadata;
  shopifyMetadata?: IShopifyMerchantMetadata;
  sessionHealth?: IMerchantSessionHealth | null;
  magentoMetadata?: IMagentoMerchantMetadata;
  salesforceMetadata?: ISalesforceMerchantMetadata;

  lastCouponCount?: number;
  lastCouponCheck?: Date;

  statusType?: MerchantStatusType;
  statusMessage?: string;
  statusUpdatedAt?: Date;

  /** Consecutive-failure breaker. `deactivatedAt` set means the merchant was
   *  switched off automatically and needs a manual reset. */
  runHealth?: {
    consecutiveFailures?: number;
    lastFailureAt?: Date | null;
    lastFailureReason?: string | null;
    /** HelpKind of the most recent failure — what a person could do about it. */
    lastHelpKind?: string | null;
    lastSuccessAt?: Date | null;
    deactivatedAt?: Date | null;
    deactivatedReason?: string | null;
  } | null;
  merchantType?: MerchantType | null;

  siteSuitabilityCache?: (SiteSuitabilityResult & { evaluatedAt: Date }) | null;

  cookies?: string; // JSON array of browser extension cookies for session injection

  /** correlationId of the auto-coupon run whose captured session is replayable. */
  sessionRunId?: string | null;
  sessionCheckoutUrl?: string | null;

  /** Cumulative AI spend tied to this merchant across every pipeline (capture runs, live/audit agentic runs, selector-recovery, session-replay repair) — NOT per-run, a durable rolling total. Informational only, no enforcement. */
  aiSpend?: { totalCost: number; updatedAt: Date | null } | null;

  /** Independent per-half status for whether an AI-built template exists for this merchant yet — auto and sku are reviewed/approved independently elsewhere in this codebase, so track them independently here too. */
  autoTemplateCreationStatus?: TTemplateCreationStatus;
  skuTemplateCreationStatus?: TTemplateCreationStatus;
}

export interface IMerchantSimple extends IUpsertMerchant {
  _id: string;
}

export enum MerchantType {
  E_COMMERCE = "E_COMMERCE",
  TRAVEL_BOOKING = "TRAVEL_BOOKING",
  PROPERTY_BUYING = "PROPERTY_BUYING",
  INSURANCE = "INSURANCE",
  FERRY_BOOKING = "FERRY_BOOKING",
  PARKED_DOMAIN = "PARKED_DOMAIN",
  DOMAIN_HOSTING = "DOMAIN_HOSTING",
  MEAL_PLANNING = "MEAL_PLANNING",
  THEME_PARK_BOOKING = "THEME_PARK_BOOKING",
  HOLIDAY_PARK_BOOKING = "HOLIDAY_PARK_BOOKING",
  LUXURY_VACATION_DEALS = "LUXURY_VACATION_DEALS",
  GAMBLING = "GAMBLING",
  THERAPY_SERVICES = "THERAPY_SERVICES",
  TRAINING_COURSES = "TRAINING_COURSES",
}

export enum checkoutTechnologyType {
  SHOPIFY = "shopify",
  WOOCOMMERCE = "woocommerce",
  MAGENTO = "magento",
  BIGCOMMERCE = "bigcommerce",
  SALESFORCE = "salesforce",
  /** Detection only for now — no API handler, so these still walk. */
  SHOPWARE = "shopware",
  OTHER = "other",
}

export interface ICheckoutDefaultSelectors {
  type: checkoutTechnologyType;
  selectors: {
    [key in ActionType]?: string[];
  };
}

export enum MerchantStatusType {
  NEW = "NEW",
  TEMPLATE_ISSUE = "TEMPLATE_ISSUE",
  NO_COUPONS = "NO_COUPONS",
  HEALTHY = "HEALTHY",
  DEV_WORK_REQUIRED = "DEV_WORK_REQUIRED",
  PROXY_NEEDED = "PROXY_NEEDED",
  SITE_NOT_SUITABLE = "SITE_NOT_SUITABLE",
  REQUIRES_LOGIN = "REQUIRES_LOGIN",
  FLIGHT_TRAVEL_BOOKING_FORMS = "FLIGHT_TRAVEL_BOOKING_FORMS",
  /** Too many consecutive failed runs. Switched off deliberately so the estate
   *  stops burning capacity on something that is not working, and left for a
   *  person to look at and reset — an automatic retry is what produced the
   *  repeated failures in the first place. */
  DEACTIVATED_AFTER_FAILURES = "DEACTIVATED_AFTER_FAILURES",
  /** Another merchant covers the same site and market — same host bar `www.`,
   *  same country once GB/UK are normalised. Switched off rather than deleted so
   *  the history stays and the decision is reversible. */
  DUPLICATE = "DUPLICATE",
  /** Every product the run could reach was out of stock. Nothing is broken in
   *  the template — the merchant needs a purchasable test product. Set so the
   *  Merchants screen can be filtered for the ones a human should re-point. */
  PRODUCT_UNAVAILABLE = "PRODUCT_UNAVAILABLE",
}

export interface IDiscoveredProduct {
  productLink: string;
  /**
   * Further product pages the agent judged testable, best first, excluding
   * `productLink`. Cached so later runs can go straight to a product without
   * paying for discovery or driving the merchant's search at all.
   */
  alternateProductLinks?: string[];
  searchKeywords?: string[];
  skipSkuSelection: boolean;
  skuOptions?: SkuOption[];
  searchBoxSelector?: string;
  searchBoxIsToggleable?: boolean;
  searchToggleSelector?: string;
  successfullyFound: boolean;
  reason?: string;
}

export interface ICheckoutDiscoveryPage {
  checkoutNextStepSelector?: string;
  checkoutUrlPattern?: string;
  checkoutUrl?: string;
  cartUrlPattern?: string;
  cartUrl?: string;
  cartAccessSelector?: string;
  couponFormExistsOnCartPage?: boolean;
  successfullyFound: boolean;
  couponFieldSelector?: string;
  toggleCouponFormSelector?: string;
  noProductsInCart?: boolean;
  /** Going on from the cart led to a sign-in page with no guest route. */
  checkoutRequiresLogin?: boolean;
  reason?: string;
}

export interface ICouponApplicationResult {
  toggleCouponBoxSelector?: string;
  couponFieldSelector?: string;
  applyCouponButtonSelector?: string;
  totalBasketAmountSelector?: string;
  couponFailureSelector?: string;
  noCouponApplicationFound?: boolean;
  notCheckoutPage?: boolean;
  couponApplicationSuccessful: boolean;
  reason: string;
}

/**
 * Whether a stored session actually works.
 *
 * A session expiring is a silent, permanent failure today: nothing records that
 * it stopped authenticating, so the merchant's coupon testing just fails from
 * then on. These fields are what make "does this merchant need a new session"
 * answerable without probing anything — every run already produces the signal,
 * it simply was not being remembered.
 */
export interface ISessionHealth {
  capturedAt: Date;
  /**
   * Last time a run authenticated with it. NULL means UNPROVEN — captured but
   * never once used successfully, which is a different state from "worked
   * yesterday, failed today" and needs treating differently.
   */
  lastOkAt?: Date | null;
  lastFailedAt?: Date | null;
  /**
   * Reset to 0 on success. The failure signal is noisy — an expired session,
   * a network blip and a platform 500 all surface the same way — so this is
   * compared against a threshold rather than trusted at 1.
   */
  consecutiveFailures?: number;
  /**
   * How many times this session has been REPLACED and still gone on to fail.
   *
   * The repair loop already works: three failures mark the session dead, the
   * schedule is brought forward and a capture stores fresh credentials. What
   * was missing is any memory of having done that, so a merchant whose platform
   * contract has genuinely changed cycled through replace-and-fail forever.
   * This is that memory, and only a run that actually succeeds clears it.
   */
  replacements?: number;
  /**
   * Set once the path is confidently NOT going to heal itself: replaced
   * `QUARANTINE_AFTER_REPLACEMENTS` times and still failing. The path stops
   * being chosen and the merchant is listed for a developer — at that point the
   * likely cause is the platform's implementation changing, not this merchant.
   *
   * Cleared by a person, which also resets `replacements`.
   */
  quarantinedAt?: Date | null;
  /** Why, plus the last error seen. The first thing a developer reads. */
  quarantineReason?: string | null;
}

/**
 * Tracked per session KIND, because a merchant can hold both and they fail
 * independently: the API credentials can expire while a replayable browser
 * session is still fine, and vice versa.
 */
export interface IMerchantSessionHealth {
  /** Platform API credentials — `shopifyMetadata` and friends. */
  api?: ISessionHealth | null;
  /** Replayable browser session — the one `sessionRunId` points at. */
  replay?: ISessionHealth | null;
}

export interface IWooCommerceMerchantMetadata {
  referer: string;
  origin: string;
  applyCouponNonce: string;
  removeCouponNonce: string;
  cookies: string;
  type: "v1" | "v2";
}

export interface IBigCommerceMerchantMetadata {
  xsrfToken: string;
  cookies: string;
  referer: string;
  origin: string;
}

export interface IShopifyMerchantMetadata {
  referer: string;
  origin: string;
  cookies: string;
  sessionToken: string;
  /**
   * When the browser walk last harvested this session. Checkout sessions expire,
   * so a stale record is an API path that will start failing — this makes the
   * age visible instead of leaving it to be discovered by a failed run.
   */
  capturedAt?: Date | null;
}

export interface IMagentoMerchantMetadata {
  referer: string;
  origin: string;
  cookies: string;
  formKey: string;
  postUrl: string;
  userAgent: string;
  version: "v1" | "v2";
}

export interface ISalesforceMerchantMetadata {
  referer: string;
  origin: string;
  cookies: string;
  csrfToken: string;
  couponPostUrl: string;
  userAgent: string;
}


/** One candidate row inside a duplicate group. */
export interface IDuplicateCandidate {
  _id: string;
  name?: string;
  homePageUrl: string;
  countryCode: string;
  checkoutType?: string;
  statusType?: string;
  scheduleEnabled: boolean;
  hasSession: boolean;
  hasApiCredentials: boolean;
  lastRunAt?: Date | null;
  couponResults: number;
  createdAt?: Date;
  /** Why this row is (or is not) the suggested keeper. */
  score: number;
  suggested: boolean;
}

export interface IDuplicateGroup {
  /** Normalised domain + country the rows share. */
  key: string;
  domain: string;
  countryCode: string;
  candidates: IDuplicateCandidate[];
  /** How many of the rows are currently scheduled — the double-run count. */
  scheduledCount: number;
}

export interface IDuplicateGroupsResponse {
  items: IDuplicateGroup[];
  total: number;
  offset: number;
  limit: number;
  /** Across the whole estate, not just this page. */
  totals: { groups: number; redundantRows: number; doubleScheduled: number };
}


/**
 * The API-quarantine review queue.
 *
 * Grouped by platform + error rather than listed per merchant, because the
 * merchant is rarely the story: a change in a platform handler shows up as
 * dozens of merchants reporting the same thing on the same day.
 */
export interface IApiQuarantineGroup {
  checkoutType: string;
  /** Normalised error — ids, counts and quoted values stripped. */
  reason: string;
  merchants: number;
  examples: Array<{ id: string; domain: string; since: string | Date | null }>;
}

export interface IApiQuarantineResponse {
  groups: IApiQuarantineGroup[];
  total: number;
}
